import Icon from "@/components/Icon/Icon";
import { Text } from "@/components/Typography/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { Option, UIState } from "./DynamicSelectField.type";

export const ReadyStatePresentation = ({
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  error,
  placeholder,
  testId,
  options,
  uiState,
  getSelectedOptionLabel,
  currentValueLabel,
  readOnlyDisplayValue,
}: {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string | null;
  placeholder?: string;
  testId?: string;
  options: Option[];
  uiState: Extract<UIState, { type: "ready" }>;
  getSelectedOptionLabel: (value: string) => string;
  currentValueLabel: string;
  readOnlyDisplayValue: string | undefined;
}) => {
  const { t } = useTranslation(defaultNS);
  const tc = useCommonTranslation();

  return (
    <div className="flex items-center justify-center w-full gap-4">
      {/* 現在の値（読み取り専用のテキスト表示） */}
      <div className="flex-1 flex justify-start min-w-0">
        <Text
          variant="labelSemiLarge"
          className="text-neutral-on-fill break-words"
        >
          {currentValueLabel}
        </Text>
      </div>

      {/* Arrow Icon */}
      <div className="flex items-center flex-shrink-0 self-center pr-3 pl-3">
        <Icon
          iconName="ArrowBigRightIcon"
          className="w-4 h-4 text-primary-on-neutral-fill"
        />
      </div>

      {/* 新しい値の選択 */}
      <div className="flex-1 min-w-0">
        {readOnly ? (
          // readonly時は選択された値のlabelを表示
          <Text
            variant="labelSemiLarge"
            className="text-neutral-on-fill break-words"
          >
            {readOnlyDisplayValue || tc("unregistered")}
          </Text>
        ) : (
          // 通常時はSelectコンポーネントを表示
          <Select
            value={value}
            onValueChange={onChange}
            disabled={disabled || readOnly || uiState.isLoading}
            onOpenChange={(open) => {
              if (open && onFocus) onFocus();
              if (!open && onBlur) onBlur();
            }}
          >
            <SelectTrigger
              className={`w-full ${error ? "border-error-fill" : ""}`}
              data-testid={testId}
              data-role="form-field-input"
              aria-invalid={!!error}
              disabled={readOnly}
            >
              <SelectValue
                placeholder={
                  uiState.isLoading
                    ? t("common.loading")
                    : uiState.placeholder ||
                      placeholder ||
                      t("common.pleaseSelect")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {uiState.isEmpty ? (
                <Text
                  variant="inputLabel"
                  className="px-3 py-2 text-warning-fill"
                >
                  {t("common.noOptions")}
                </Text>
              ) : (
                options.map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    title={item.description || undefined}
                  >
                    {getSelectedOptionLabel(item.value)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};
